import React, { useState } from 'react';
import { Box, Dialog, DialogContent, DialogActions, Typography } from '@mui/material';
import { toast } from 'react-toastify';
import { API } from '../../../../api';
import { ProgramSummary } from './ProgramsView';
import CommonModalHeader from '../../../../components/DialogUi/CommonModalHeader';
import DosenInput from '../../components/DosenInput';
import DosenAutocomplete from '../../components/DosenAutocomplete';
import DosenButton from '../../../../components/ui/Buttons/DosenButton';
import { events, attributeKeys } from '../../../../utils/amplitude';
import { trackWithBaseProps } from '../../../../utils/amplitudeProductTracking';
import { useRoleLabels } from '../../../../utils/memberTypeLabels';
import { MOBILE_SX, useIsMobileApp } from '../../../../mobile/mobileApp';
import BulkInvitePanel from './BulkInvitePanel';

interface InviteMemberDialogProps {
  open: boolean;
  onClose: () => void;
  authId: string;
  programs: ProgramSummary[];
  /** Emails of the invitations still pending: Bulk upload skips them. */
  invitedEmails?: string[];
  onInvited?: () => void;
}

type InviteWay = 'one' | 'bulk';

const WayButton: React.FC<{ label: string; selected: boolean; onClick: () => void }> = ({
  label,
  selected,
  onClick
}) => (
  <Box
    component="button"
    type="button"
    aria-pressed={selected}
    onClick={onClick}
    sx={{
      flex: 1,
      py: '8px',
      borderRadius: '6px',
      border: `1px solid ${selected ? '#F79420' : '#2A2A2A'}`,
      bgcolor: selected ? 'rgba(247, 148, 32, 0.12)' : '#151515',
      color: selected ? '#FDFDFD' : '#B6B6B6',
      fontFamily: 'Inter, sans-serif',
      fontSize: 13,
      fontWeight: 600,
      cursor: 'pointer'
    }}
  >
    {label}
  </Box>
);

interface RoleOption {
  value: string;
  label: string;
}

/**
 * The role a person is invited as. `value` is the canonical role the backend
 * stores and compares — it never changes. `label` is only what the admin
 * reads, and an org that renamed its roles in Org Settings sees its own words
 * ("Employee", "Manager") instead of ours. Built per render from the org's
 * labels; see useRoleLabels.
 */
const useRoleOptions = (): RoleOption[] => {
  const labels = useRoleLabels();
  return [
    { value: 'mentee', label: labels.mentee },
    { value: 'mentor', label: labels.mentor },
    { value: 'admin', label: labels.admin }
  ];
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const subcopySx = { fontSize: 12, color: '#868686', fontFamily: 'Inter, sans-serif', mt: 0.5 };

/**
 * Org-first manual invite (D2): invites a person to the ORGANIZATION (no
 * program required). They appear in the members list once they accept;
 * program allocation is a separate action for existing members. Consumes
 * POST /org/{authId}/members/invite (BE-2). Bulk upload invites everyone in
 * a CSV or Excel file the same way (BulkInvitePanel).
 */
const InviteMemberDialog = ({
  open,
  onClose,
  authId,
  programs,
  invitedEmails = [],
  onInvited
}: InviteMemberDialogProps) => {
  const ROLES = useRoleOptions();
  const labels = useRoleLabels();
  const [way, setWay] = useState<InviteWay>('one');
  const [bulkBusy, setBulkBusy] = useState(false);
  // A form: the whole screen on a phone.
  const isMobileApp = useIsMobileApp();
  // A program for its roles only takes its learners through their roles, never by invitation (the backend refuses it).
  const invitable = React.useMemo(() => programs.filter((p) => !p.rolesOnly), [programs]);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [roles, setRoles] = useState<RoleOption[]>([ROLES[0]]);
  const [selectedPrograms, setSelectedPrograms] = useState<ProgramSummary[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setFirstName('');
    setLastName('');
    setEmail('');
    setRoles([ROLES[0]]);
    setSelectedPrograms([]);
  };

  const handleClose = () => {
    if (submitting || bulkBusy) return;
    reset();
    onClose();
  };

  const valid =
    firstName.trim().length > 0 && lastName.trim().length > 0 && EMAIL_RE.test(email.trim()) && roles.length > 0;

  const handleSubmit = async () => {
    // The Send Invite button stays disabled until the required fields are filled.
    if (!valid) return;
    setSubmitting(true);
    try {
      await API.inviteOrgMember(authId, {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim().toLowerCase(),
        role: roles.map((r) => r.value),
        programIds: selectedPrograms.map((p) => p.id)
      });
      trackWithBaseProps(events.userInvited, {
        // The INVITEE's email/role (the inviter's context comes from base props).
        inviteeEmail: email.trim().toLowerCase(),
        // The canonical role, not the org's label: otherwise the same role
        // arrives as a different value per org and the funnel splits.
        inviteeRole: roles.map((r) => r.value).join(', '),
        [attributeKeys.enrollmentType]: 'Manual Invite',
        [attributeKeys.programName]: selectedPrograms.map((p) => p.name).join(', ') || undefined
      });
      toast.success('Invitation sent');
      onInvited?.();
      reset();
      onClose();
    } catch (e: any) {
      const msg = e?.response?.data?.message || 'The invitation could not be sent. Try again.';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      fullScreen={isMobileApp}
      PaperProps={{
        sx: {
          bgcolor: '#0F0F0F',
          color: '#FDFDFD',
          border: '1px solid #2A2A2A',
          borderRadius: 2,
          width: way === 'bulk' ? 760 : 480,
          maxWidth: '90vw',
          [MOBILE_SX]: { width: '100%', maxWidth: '100%', m: 0, border: 'none', borderRadius: 0 }
        }
      }}
    >
      <CommonModalHeader title="Invite people" handleCloseModal={handleClose} />
      <DialogContent style={{ backgroundColor: '#0F0F0F' }}>
        <Box sx={{ display: 'flex', gap: 1, pt: 1, pb: 2.5 }}>
          <WayButton label="One person" selected={way === 'one'} onClick={() => !bulkBusy && setWay('one')} />
          <WayButton label="Bulk upload" selected={way === 'bulk'} onClick={() => !submitting && setWay('bulk')} />
        </Box>
        {way === 'bulk' ? (
          <BulkInvitePanel
            authId={authId}
            labels={labels}
            invitedEmails={invitedEmails}
            onInvited={() => onInvited?.()}
            onClose={handleClose}
            onBusyChange={setBulkBusy}
          />
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <Box sx={{ display: 'flex', gap: 2, [MOBILE_SX]: { flexDirection: 'column', gap: 2.5 } }}>
              <DosenInput
                label="First name*"
                placeholder="John"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
              />
              <DosenInput
                label="Last name*"
                placeholder="Smith"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
              />
            </Box>

            <DosenInput
              label="Email*"
              placeholder="john.smith@gmail.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />

            <Box>
              <DosenAutocomplete
                label="Role*"
                multiple
                options={ROLES}
                value={roles}
                onChange={(_: React.SyntheticEvent, value: RoleOption[]) => setRoles(value)}
                getOptionLabel={(option: RoleOption) => option?.label ?? ''}
                isOptionEqualToValue={(a: RoleOption, b: RoleOption) => a.value === b.value}
              />
              <Typography sx={subcopySx}>Choose one or multiple roles for the person you’re inviting</Typography>
            </Box>

            <Box>
              <DosenAutocomplete
                label="Program"
                multiple
                options={invitable}
                value={selectedPrograms}
                onChange={(_: React.SyntheticEvent, value: ProgramSummary[]) => setSelectedPrograms(value)}
                getOptionLabel={(option: ProgramSummary) => option?.name ?? ''}
                isOptionEqualToValue={(a: ProgramSummary, b: ProgramSummary) => a.id === b.id}
              />
              <Typography sx={subcopySx}>
                If you have auto-enrollment set up then you can leave this field empty. If not then select the you want
                to enroll this person in.
              </Typography>
            </Box>
          </Box>
        )}
      </DialogContent>

      {way === 'one' && (
        <DialogActions style={{ backgroundColor: '#0F0F0F' }}>
          <DosenButton
            tone="cta"
            onClick={handleSubmit}
            disabled={submitting || !valid}
            loading={submitting}
            loadingText="Inviting..."
          >
            Send Invite
          </DosenButton>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default InviteMemberDialog;
